import {
    useCallback, useMemo,
} from 'react'

import SkyNetTable from 'app/shared-components/SkyNetTable'
import {
    TableControlTypes,
} from 'app/shared-components/TableControls'
import {
    RoutesConfig,
} from 'app/SkyNetRoutes/SkyNetRoutes.types'

import usePricingRoutes from 'app/SkyNetRoutes/hooks/usePricingRoutes'
import quoteRequests from 'app/requests/Quote/Quote.requests'
import laneRequests from 'app/requests/Lane/Lane.requests'
import laneFeesRequests from 'app/requests/ContractBasis/LaneFees.requests'

import useStyles from './Dashboard.style'
import newQuotesConfig from './config/newQuotes.config'
import beingPricedConfig from './config/beingPriced.config'
import pricingCompletedConfig from './config/completed.config'
import lanesConfig from './config/lanes.config'
import laneFeesConfig from './config/laneFees.config'

const DOMAIN_NAME = 'Dashboard'
const aQuotesUniqField = 'quoteNumber'

const tableControls: TableControlTypes[] = [
    TableControlTypes.ClearFilters,
    TableControlTypes.ClearSorting,
]

const Dashboard = () => {
    const {
        AQuotes: AQuotesRoutes,
        LaneFees: LaneFeesRoutes,
    } = usePricingRoutes()

    const {
        classes,
    } = useStyles()

    const urls = useMemo(() => {
        return Object.fromEntries(Object.entries({
            pricingBeingPriced: quoteRequests.pricingBeingPriced,
            pricingCompleted: quoteRequests.pricingCompleted,
            newQuotes: quoteRequests.newQuotes,
            lanes: laneRequests.toBePriced,
            laneFees: laneFeesRequests.filterRequestExpireSoon,
        }).map(([
            key,
            fn,
        ]) => {
            return [
                key,
                fn()?.url,
            ]
        }))
    }, [])

    const onAQuotesRowClick = useCallback(({
        id,
    }: { id: number }) => {
        return AQuotesRoutes.Overview.navigate({
            params: {
                key: id,
            },
        })
    }, [AQuotesRoutes])

    const onLaneRowClick = useCallback(({
        quoteId,
        laneGroupId,
    }: {
        quoteId: number,
        laneGroupId: number,
    }) => {
        if (laneGroupId) {
            return (AQuotesRoutes.AssignPricing as RoutesConfig).LaneFees.navigate({
                params: {
                    key: quoteId,
                    laneGroupId,
                },
            })
        }

        return AQuotesRoutes.Overview.navigate({
            params: {
                key: quoteId,
            },
        })
    }, [AQuotesRoutes])

    const onLaneFeesRowClick = useCallback(({
        id,
    }: { id: number }) => {
        return LaneFeesRoutes.Overview.navigate({
            params: {
                key: id,
            },
        })
    }, [LaneFeesRoutes.Overview])

    return (
        <div className={classes.root}>
            <div className={classes.headerTitle}>
                Quotes
                <div className={classes.actionWrapper} />
            </div>
            <div className={classes.tableWrapper}>
                <SkyNetTable
                    name={DOMAIN_NAME}
                    onRowClick={onAQuotesRowClick}
                    customUrl={urls.newQuotes}
                    tableConfig={newQuotesConfig}
                    tableControls={tableControls}
                    uniqField={aQuotesUniqField}
                    isShowCounterTab
                    showSearch
                    paginationModeMatches
                />
            </div>
            <div className={classes.tableWrapper}>
                <SkyNetTable
                    name={DOMAIN_NAME}
                    onRowClick={onAQuotesRowClick}
                    customUrl={urls.pricingBeingPriced}
                    tableConfig={beingPricedConfig}
                    tableControls={tableControls}
                    uniqField={aQuotesUniqField}
                    isShowCounterTab
                    showSearch
                    paginationModeMatches
                />
            </div>
            <div className={classes.tableWrapper}>
                <SkyNetTable
                    name={DOMAIN_NAME}
                    onRowClick={onAQuotesRowClick}
                    customUrl={urls.pricingCompleted}
                    tableConfig={pricingCompletedConfig}
                    tableControls={tableControls}
                    uniqField={aQuotesUniqField}
                    isShowCounterTab
                    showSearch
                    paginationModeMatches
                />
            </div>
            <div className={classes.headerTitle}>
                Lane Fees
                <div className={classes.actionWrapper} />
            </div>
            <div className={classes.tableWrapper}>
                <SkyNetTable
                    name={DOMAIN_NAME}
                    moduleName="LaneFees"
                    onRowClick={onLaneFeesRowClick}
                    customUrl={urls.laneFees}
                    tableConfig={laneFeesConfig}
                    tableControls={tableControls}
                    uniqField="laneFeesNumber"
                    isShowCounterTab
                    showSearch
                    paginationModeMatches
                />
            </div>
            <div className={classes.headerTitle}>
                Lanes
                <div className={classes.actionWrapper} />
            </div>
            <div className={classes.tableWrapper}>
                <SkyNetTable
                    name={DOMAIN_NAME}
                    moduleName="Lanes"
                    onRowClick={onLaneRowClick}
                    customUrl={urls.lanes}
                    tableConfig={lanesConfig}
                    tableControls={tableControls}
                    uniqField="laneNumber"
                    isShowCounterTab
                    showSearch
                    paginationModeMatches
                />
            </div>
        </div>
    )
}

export default Dashboard
